import { PropsWithChildren } from 'react';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { localStorageHelper } from '@frontend/auth-helpers';
import { createPersistProvider } from '@frontend/store';
import { SCHEDULE_LOCAL_STORAGE_KEYS } from '../constants';
import { resetCalendarLocationStoreOnLogin } from './calendar-location.store';
import { SyncSchedulePulseWithCalendarWrapper } from './schedule-pulse.store';

export type CalendarEntityType = 'provider' | 'operatory';

type State = {
  isForSchedulePulse: boolean;
  shouldShowLocationSelectorModal: boolean;
  calendarDate: string; // MM/DD/YYYY
  calendarEntityType: CalendarEntityType;
  _uniqueKeyForFilter: string; // internal use only, to track unique key for filter reset
  calendarProviderIds: string[];
  calendarApptTypeIds: string[];
  calendarApptStatuses: string[];
  calendarOperatories: string[];
  calendarInsuranceStatuses: string[];
};

type Actions = {
  setIsForSchedulePulse: (isForSchedulePulse: boolean) => void;
  setShowLocationSelectorModal: (shouldShow: boolean) => void;
  setCalendarDate: (calendarDate: string) => void;
  setCalendarEntityType: (calendarEntityType: CalendarEntityType) => void;
  setCalendarFilters: (filters: {
    providerIds?: string[];
    apptTypeIds?: string[];
    apptStatuses?: string[];
    operatories?: string[];
    insuranceStatuses?: string[];
  }) => void;
  resetCalendarFilters: (locationIds?: string[]) => void;
};

type Store = State & Actions;

const initialState: State = {
  isForSchedulePulse: false, // for side action-bar schedule-pulse case we set this to true
  shouldShowLocationSelectorModal: false,
  calendarDate: '', // default to empty string, will be set to today's date in the component based on location timezone
  calendarEntityType: 'provider',

  // filter related info
  _uniqueKeyForFilter: '',
  calendarProviderIds: [],
  calendarApptTypeIds: [],
  calendarApptStatuses: [],
  calendarOperatories: [],
  calendarInsuranceStatuses: [],
};

const { Provider, useStore: useCalendarStore } = createPersistProvider<Store>()((set, get) => ({
  ...initialState,
  setIsForSchedulePulse: (isForSchedulePulse) => set({ isForSchedulePulse }),
  setShowLocationSelectorModal: (shouldShow) => set({ shouldShowLocationSelectorModal: shouldShow }),
  setCalendarDate: (calendarDate) => set({ calendarDate }),
  setCalendarEntityType: (calendarEntityType) => set({ calendarEntityType }),

  setCalendarFilters: ({
    providerIds = [],
    apptTypeIds = [],
    apptStatuses = [],
    operatories = [],
    insuranceStatuses = [],
  }) => {
    set({
      calendarProviderIds: providerIds,
      calendarApptTypeIds: apptTypeIds,
      calendarApptStatuses: apptStatuses,
      calendarOperatories: operatories,
      calendarInsuranceStatuses: insuranceStatuses,
    });
  },
  resetCalendarFilters: (locationIds = []) => {
    const newKey = locationIds.slice().sort().join(',') || '';
    const oldKey = get()._uniqueKeyForFilter;
    if (newKey && oldKey === newKey) {
      // no change in location ids, so no need to reset the filters
      return;
    }
    set({
      calendarProviderIds: [],
      calendarApptTypeIds: [],
      calendarApptStatuses: [],
      calendarOperatories: [],
      calendarInsuranceStatuses: [],
      _uniqueKeyForFilter: newKey,
    });
  },
}));

const CalendarProvider = ({ children, isForSchedulePulse }: PropsWithChildren<{ isForSchedulePulse?: boolean }>) => (
  <Provider
    persistOptions={{
      name: isForSchedulePulse
        ? SCHEDULE_LOCAL_STORAGE_KEYS.SCHEDULE_PULSE_STORE
        : SCHEDULE_LOCAL_STORAGE_KEYS.SCHEDULE_CALENDAR_STORE,
    }}
  >
    {isForSchedulePulse ? (
      <SyncSchedulePulseWithCalendarWrapper>{children}</SyncSchedulePulseWithCalendarWrapper>
    ) : (
      children
    )}
  </Provider>
);

const resetCalendarStoreOnLogin = () => {
  // reset calendar date for calendar page
  const existingState = localStorageHelper.get<{ state?: Store }>(SCHEDULE_LOCAL_STORAGE_KEYS.SCHEDULE_CALENDAR_STORE);
  if (existingState?.state?.calendarDate) {
    existingState.state.calendarDate = '';
    localStorageHelper.create(SCHEDULE_LOCAL_STORAGE_KEYS.SCHEDULE_CALENDAR_STORE, JSON.stringify(existingState));
  }

  // reset whole state for schedule pulse
  localStorageHelper.delete(SCHEDULE_LOCAL_STORAGE_KEYS.SCHEDULE_PULSE_STORE);
};

// TODO: add wait mechanism to handle this within component without global listener
SchedulerV3.addScheduleCustomEventListener('schedule:post-login', () => {
  resetCalendarStoreOnLogin();
  resetCalendarLocationStoreOnLogin();
});

export { CalendarProvider, useCalendarStore };
